/**
 * 校验原语。只放纯函数，**不引第三方 schema 库**——嵌入端要保持产物体积和审计面都小。
 *
 * `maxLength` 一律必填：父→子和子→父的上限不同（见 constants.ts），
 * 留默认值就等于让调用点静默拿到另一个方向的宽度。
 */
export function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
/** 非空且不超长的字符串，否则 null。 */
export function readText(value, maxLength) {
    return typeof value === "string" && value.length > 0 && value.length <= maxLength ? value : null;
}
/** 落在允许集合内的字面量，否则 null。 */
export function readEnum(value, allowed, maxLength) {
    const text = readText(value, maxLength);
    return text !== null && allowed.includes(text) ? text : null;
}
/** 单调递增序号：正的安全整数。 */
export function readSeq(value) {
    return typeof value === "number" && Number.isSafeInteger(value) && value > 0 ? value : null;
}
/** 像素尺寸：非负有限数，向上取整并封顶。 */
export function readSize(value, maxValue) {
    return typeof value === "number" && Number.isFinite(value) && value >= 0 && value <= maxValue
        ? Math.ceil(value)
        : null;
}
/** 不可信文本，截断后按纯文本渲染。绝不作为 HTML 注入页面（§12.3）。 */
export function truncateText(value, maxLength) {
    return typeof value === "string" ? value.slice(0, maxLength) : "";
}
/**
 * 站内相对路径的基础校验。
 *
 * 这些路径会被拼进宿主 URL（`navigation:changed`）、用于打开新标签页
 * （`navigation:open-standalone`）或还原 iframe 路由（`initialPath`）。
 * 放行 `//evil.com`、`javascript:` 或 `..` 就等于把开放重定向送出去。
 */
export function readSafeRelativePath(value, maxLength) {
    const raw = readText(value, maxLength);
    if (raw === null || !raw.startsWith("/") || raw.startsWith("//") || raw.includes("\\")) {
        return null;
    }
    try {
        // URL 会把 `%2e%2e` 规范化成 `..`；在交给它之前检查每个解码后的 path segment，
        // 否则 `/embedded/%2e%2e/admin` 会先过前缀校验、导航时再变成 `/admin`。
        const rawPathname = raw.split(/[?#]/, 1)[0] ?? "";
        const hasDotSegment = rawPathname
            .split("/")
            .some((segment) => {
            const decoded = decodeURIComponent(segment);
            return decoded === "." || decoded === "..";
        });
        if (hasDotSegment) {
            return null;
        }
        const baseOrigin = "https://relative-path.invalid";
        const parsed = new URL(raw, baseOrigin);
        if (parsed.origin !== baseOrigin) {
            return null;
        }
        const normalized = `${parsed.pathname}${parsed.search}${parsed.hash}`;
        return normalized.length <= maxLength ? normalized : null;
    }
    catch {
        return null;
    }
}
/**
 * 在基础校验之上再要求路径落在某个前缀下，并剥掉 hash。
 *
 * 用于 iframe 侧接收宿主下发的深链：只允许 `/embedded/**`，
 * 越出白名单前缀的一律丢弃。返回值保留 query，不含 hash。
 */
export function createRoutePrefixValidator(routePrefix, maxLength) {
    return (value) => {
        const raw = readSafeRelativePath(value, maxLength);
        if (raw === null) {
            return null;
        }
        const path = raw.split("#")[0] ?? "";
        const pathname = path.split("?")[0] ?? "";
        if (pathname !== routePrefix && !pathname.startsWith(`${routePrefix}/`)) {
            return null;
        }
        return path;
    };
}
//# sourceMappingURL=primitives.js.map